import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Chart from 'react-apexcharts';
import {
    MdBarChart, MdPieChart, MdTrendingUp, MdAttachMoney,
    MdFilterList, MdInventory, MdKeyboardArrowDown, MdWarning,
    MdInsertChartOutlined, MdArrowForward, MdShowChart, MdCalendarToday, MdKeyboardArrowLeft, MdKeyboardArrowRight
} from 'react-icons/md';

import { productsAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YARDIMCI FONKSİYONLAR ---
const formatCurrency = (value) => {
    return (Number(value) || 0).toLocaleString('tr-TR', {
        style: 'currency',
        currency: 'TRY',
    });
};

const monthNames = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const PAGE_SIZE = 5;

// --- SKELETON LOADERS ---
const SummarySkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-pulse mt-4">
        {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 w-full rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        ))}
    </div>
);

const ChartSkeleton = () => (
    <div className="h-80 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50 animate-pulse mt-6" />
);

const ListSkeleton = () => (
    <div className="flex flex-col gap-4 animate-pulse mt-6">
        <div className="h-12 w-full rounded-xl bg-gray-200 dark:bg-navy-700" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 w-full rounded-[20px] bg-gray-100 dark:bg-navy-900/50" />
            ))}
        </div>
    </div>
);

const AdminStockAnalytics = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11

    // --- STATELER (CoreUI İLE BİREBİR) ---
    const [products, setProducts] = useState([]);
    const [salesHistory, setSalesHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filtreler
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth + 1); // 1-12
    const [filterMode, setFilterMode] = useState('monthly'); // 'monthly' veya 'all'

    // Sayfalama (Her tablo için ayrı sayfalama - CoreUI ile birebir)
    const [currentPage, setCurrentPage] = useState({
        topSelling: 1,
        topRevenue: 1,
        topProfit: 1,
    });

    const { logout } = useAuth();
    const handleAuthError = useCallback(() => logout(), [logout]);

    // --- 1. VERİ ÇEKME (FRONTEND'DE TOPLANACAK) ---
    useEffect(() => {
        let mounted = true;
        const fetchAnalyticsData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [productsRes, salesRes] = await Promise.all([
                    productsAPI.getAll(),
                    productsAPI.getSalesHistory(),
                ]);

                if (mounted) {
                    if (!productsRes.success || !salesRes.success) {
                        throw new Error('Analiz verileri yüklenirken hata oluştu.');
                    }
                    setProducts(productsRes.products || []);
                    setSalesHistory(salesRes.sales_history || []);
                }
            } catch (err) {
                console.error('Analiz verisi yükleme hatası:', err);
                if (String(err.message).includes('401') || String(err.message).includes('Unauthorized')) {
                    handleAuthError();
                    return;
                }
                if (mounted) setError(err.message || 'Analiz verileri yüklenirken bir hata oluştu.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchAnalyticsData();
        return () => { mounted = false; };
    }, [handleAuthError]);

    // --- 2. İŞ ZEKASI HESAPLAMALARI (CoreUI İLE BİREBİR WORK-LOGIC) ---
    const analyticsData = useMemo(() => {
        if (products.length === 0) {
            return { topSelling: [], topRevenue: [], topProfit: [], filteredCount: 0 };
        }

        // A. Satışları Filtrele (Tarihe Göre)
        const filteredSales = salesHistory.filter(sale => {
            if (filterMode === 'all') return true;
            try {
                const saleDate = new Date(sale.created_at);
                const saleYear = saleDate.getFullYear();
                const saleMonth = saleDate.getMonth() + 1; // 1-12
                return saleYear === selectedYear && saleMonth === selectedMonth;
            } catch {
                return false;
            }
        });

        if (filteredSales.length === 0 && filterMode !== 'all') {
            return { topSelling: [], topRevenue: [], topProfit: [], filteredCount: 0 };
        }

        // B. Ürün Haritası
        const productMap = new Map();
        products.forEach(p => productMap.set(p.id, p));

        // C. İstatistikleri Topla (Ciro, Maliyet, Adet)
        const productStats = new Map();

        filteredSales.forEach(sale => {
            const product = productMap.get(sale.product_id);
            if (!product) return;

            const stats = productStats.get(sale.product_id) || { totalRevenue: 0, totalCost: 0, totalQuantity: 0 };

            stats.totalRevenue += parseFloat(sale.total_price);
            stats.totalCost += parseFloat(product.purchase_price) * sale.quantity_sold;
            stats.totalQuantity += sale.quantity_sold;

            productStats.set(sale.product_id, stats);
        });

        // D. Raporları Oluştur & Kâr Marjı Hesapla
        const allRankedProducts = Array.from(productStats.entries()).map(([id, stats]) => {
            const product = productMap.get(id);
            const totalProfit = stats.totalRevenue - stats.totalCost;
            const profitMargin = stats.totalRevenue > 0 ? (totalProfit / stats.totalRevenue) * 100 : 0;

            return {
                id,
                name: product?.name || 'Bilinmeyen Ürün',
                total_sold: stats.totalQuantity,
                totalRevenue: stats.totalRevenue,
                totalProfit: totalProfit,
                profitMargin: profitMargin,
            };
        });

        // E. Sıralamalar (Sort)
        const topSelling = [...allRankedProducts].sort((a, b) => b.total_sold - a.total_sold);
        const topRevenue = [...allRankedProducts].sort((a, b) => b.totalRevenue - a.totalRevenue);
        const topProfit = [...allRankedProducts].sort((a, b) => b.totalProfit - a.totalProfit);

        return { topSelling, topRevenue, topProfit, filteredCount: filteredSales.length };

    }, [products, salesHistory, selectedYear, selectedMonth, filterMode]);


    // --- 3. APEXCHARTS GRAFİK YAPILANDIRMASI ---
    const chartData = useMemo(() => {
        const createChartConfig = (data, valueKey, label, isCurrency = false) => {
            const top5 = data.slice(0, 5);
            return {
                series: [{ name: label, data: top5.map(item => item[valueKey]) }],
                options: {
                    chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif' },
                    plotOptions: { bar: { borderRadius: 6, columnWidth: '40%', distributed: true } },
                    dataLabels: { enabled: false },
                    legend: { show: false },
                    xaxis: {
                        categories: top5.map(item => item.name.length > 12 ? item.name.substring(0, 10) + '...' : item.name),
                        labels: { style: { colors: '#A3AED0', fontSize: '11px', fontWeight: '600' } },
                        axisBorder: { show: false },
                        axisTicks: { show: false }
                    },
                    yaxis: {
                        labels: {
                            style: { colors: '#A3AED0', fontSize: '11px', fontWeight: '600' },
                            formatter: (val) => isCurrency ? formatCurrency(val) : val
                        }
                    },
                    grid: { borderColor: 'rgba(163, 174, 208, 0.1)', strokeDashArray: 5 },
                    tooltip: {
                        theme: 'dark',
                        y: { formatter: (val) => isCurrency ? formatCurrency(val) : `${val} Adet` }
                    },
                    colors: ['#4318FF', '#6AD2FF', '#39B8FF', '#582CFF', '#2B3674']
                }
            };
        };

        return {
            selling: createChartConfig(analyticsData.topSelling, 'total_sold', 'Satılan Adet'),
            revenue: createChartConfig(analyticsData.topRevenue, 'totalRevenue', 'Toplam Ciro', true),
            profit: createChartConfig(analyticsData.topProfit, 'totalProfit', 'Net Kâr', true)
        };
    }, [analyticsData]);

    // --- SAYFALAMA KONTROLLERİ ---
    const handlePageChange = (reportType, diff) => {
        setCurrentPage(prev => ({ ...prev, [reportType]: prev[reportType] + diff }));
    };

    const getPagedData = (reportType) => {
        const data = analyticsData[reportType] || [];
        const page = currentPage[reportType] || 1;
        const start = (page - 1) * PAGE_SIZE;
        return data.slice(start, start + PAGE_SIZE);
    };


    // --- DİNAMİK LİSTE (KART & TABLO) OLUŞTURUCU ---
    const renderAnalyticsList = (reportType, title, icon, valueKey, valueLabel, isCurrency = false, showMargin = false) => {
        const fullData = analyticsData[reportType] || [];
        const pagedData = getPagedData(reportType);
        const totalPages = Math.ceil(fullData.length / PAGE_SIZE);

        return (
            <div className="flex flex-col bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 h-full">
                <div className="flex items-center justify-between mb-6">
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2">
                        <span className="text-brand-500 text-2xl">{icon}</span> {title}
                    </h5>
                </div>

                {fullData.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 opacity-50 mt-auto mb-auto">
                        <p className="font-bold text-navy-700 dark:text-white">Veri bulunamadı.</p>
                    </div>
                ) : (
                    <>
                        {/* MASAÜSTÜ TABLO */}
                        <div className="hidden md:block overflow-x-auto mt-auto">
                            <table className="w-full text-left">
                                <thead>
                                <tr className="border-b border-gray-100 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                    <th className="py-3 pl-2 w-[40%]">Sıra & Ürün</th>
                                    <th className="py-3 text-right">{valueLabel}</th>
                                    {showMargin && <th className="py-3 text-right pr-2">Kâr Marjı</th>}
                                </tr>
                                </thead>
                                <tbody>
                                {pagedData.map((item, idx) => {
                                    const rank = (currentPage[reportType] - 1) * PAGE_SIZE + idx + 1;
                                    return (
                                        <tr key={item.id} className="border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                            <td className="py-3 pl-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-black text-gray-300 dark:text-gray-600">#{rank}</span>
                                                    <span className="text-sm font-bold text-navy-700 dark:text-white line-clamp-1">{item.name}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 text-right">
                          <span className="px-2 py-1 rounded-md bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 text-xs font-black">
                            {isCurrency ? formatCurrency(item[valueKey]) : `${item[valueKey]} ADET`}
                          </span>
                                            </td>
                                            {showMargin && (
                                                <td className="py-3 text-right pr-2">
                             <span className={`text-xs font-bold ${item.profitMargin < 0 ? 'text-red-500' : 'text-green-500'}`}>
                                %{item.profitMargin.toFixed(1)}
                             </span>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* MOBİL KART GÖRÜNÜMÜ */}
                        <div className="grid grid-cols-1 md:hidden gap-3">
                            {pagedData.map((item, idx) => {
                                const rank = (currentPage[reportType] - 1) * PAGE_SIZE + idx + 1;
                                return (
                                    <div key={item.id} className="flex justify-between items-center p-4 rounded-[16px] bg-gray-50/50 dark:bg-navy-900/30 border border-gray-100 dark:border-white/5 relative overflow-hidden">
                                        <div className="flex-1 pr-2">
                                            <span className="text-[10px] font-black text-gray-400 block mb-1">SIRA #{rank}</span>
                                            <h6 className="text-sm font-bold text-navy-700 dark:text-white line-clamp-1">{item.name}</h6>
                                        </div>
                                        <div className="text-right">
                       <span className="text-sm font-black text-brand-500 block">
                         {isCurrency ? formatCurrency(item[valueKey]) : `${item[valueKey]} Adet`}
                       </span>
                                            {showMargin && (
                                                <span className={`text-[10px] font-bold ${item.profitMargin < 0 ? 'text-red-500' : 'text-green-500'}`}>
                           MARJ: %{item.profitMargin.toFixed(1)}
                         </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* SAYFALAMA */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-white/10">
                                <button
                                    onClick={() => handlePageChange(reportType, -1)} disabled={currentPage[reportType] === 1}
                                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 dark:border-white/10 dark:bg-navy-800 dark:text-gray-400"
                                ><MdKeyboardArrowLeft/></button>
                                <span className="text-xs font-bold text-gray-500">{currentPage[reportType]} / {totalPages}</span>
                                <button
                                    onClick={() => handlePageChange(reportType, 1)} disabled={currentPage[reportType] === totalPages}
                                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 dark:border-white/10 dark:bg-navy-800 dark:text-gray-400"
                                ><MdKeyboardArrowRight/></button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };


    // --- RENDER ---
    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BAŞLIK VE FİLTRE */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 rounded-[20px] bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 flex-none">
                        <MdPieChart className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Stok Analiz Raporları</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ürün satış performansınızı detaylı inceleyin.</p>
                    </div>
                </div>

                {/* HorizonUI Filtre Barı */}
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto bg-gray-50 dark:bg-navy-900 p-2 rounded-2xl border border-gray-100 dark:border-white/10">
                    <div className="flex items-center gap-2 px-2 text-brand-500">
                        <MdCalendarToday className="text-lg" /> <span className="text-sm font-bold text-navy-700 dark:text-white whitespace-nowrap">Dönem:</span>
                    </div>

                    <div className="relative w-full sm:w-36">
                        <select value={filterMode} onChange={(e) => { setFilterMode(e.target.value); setCurrentPage({topSelling:1, topRevenue:1, topProfit:1}); }} className="w-full h-10 appearance-none rounded-xl bg-white dark:bg-navy-800 px-3 text-sm font-bold text-navy-700 dark:text-white border border-gray-200 dark:border-white/10 outline-none cursor-pointer">
                            <option value="monthly">Aylık</option>
                            <option value="all">Tüm Zamanlar</option>
                        </select>
                        <MdKeyboardArrowDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>

                    {filterMode === 'monthly' && (
                        <>
                            <div className="relative w-full sm:w-32">
                                <select value={selectedMonth} onChange={(e) => { setSelectedMonth(parseInt(e.target.value)); setCurrentPage({topSelling:1, topRevenue:1, topProfit:1}); }} className="w-full h-10 appearance-none rounded-xl bg-white dark:bg-navy-800 px-3 text-sm font-bold text-navy-700 dark:text-white border border-gray-200 dark:border-white/10 outline-none cursor-pointer">
                                    {monthNames.map((name, index) => <option key={index} value={index + 1}>{name}</option>)}
                                </select>
                                <MdKeyboardArrowDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                            <div className="relative w-full sm:w-28">
                                <select value={selectedYear} onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setCurrentPage({topSelling:1, topRevenue:1, topProfit:1}); }} className="w-full h-10 appearance-none rounded-xl bg-white dark:bg-navy-800 px-3 text-sm font-bold text-navy-700 dark:text-white border border-gray-200 dark:border-white/10 outline-none cursor-pointer">
                                    {[...Array(5)].map((_, i) => <option key={currentYear - i} value={currentYear - i}>{currentYear - i}</option>)}
                                </select>
                                <MdKeyboardArrowDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {error && (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400 text-sm font-bold flex items-center gap-2">
                    <MdWarning className="text-xl" /> {error}
                </div>
            )}

            {/* 2. TOP CARD BÖLÜMÜ */}
            {loading ? <SummarySkeleton /> : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center justify-between p-5 rounded-[20px] bg-white dark:bg-navy-800 shadow-sm border border-gray-100 dark:border-white/10">
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">En Çok Satan</p>
                            <h5 className="text-base font-black text-navy-700 dark:text-white truncate max-w-[150px] lg:max-w-[200px]">
                                {analyticsData.topSelling[0]?.name || 'Veri Yok'}
                            </h5>
                            <span className="text-sm font-bold text-brand-500">{analyticsData.topSelling[0]?.total_sold || 0} Adet</span>
                        </div>
                        <div className="h-12 w-12 flex items-center justify-center rounded-full bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400">
                            <MdInventory className="text-2xl" />
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-5 rounded-[20px] bg-white dark:bg-navy-800 shadow-sm border border-gray-100 dark:border-white/10">
                        <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">En Yüksek Ciro</p>
                            <h5 className="text-base font-black text-navy-700 dark:text-white truncate max-w-[150px] lg:max-w-[200px]">
                                {analyticsData.topRevenue[0]?.name || 'Veri Yok'}
                            </h5>
                            <span className="text-sm font-bold text-green-500">{formatCurrency(analyticsData.topRevenue[0]?.totalRevenue)}</span>
                        </div>
                        <div className="h-12 w-12 flex items-center justify-center rounded-full bg-green-50 text-green-500 dark:bg-green-500/10 dark:text-green-400">
                            <MdAttachMoney className="text-2xl" />
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-5 rounded-[20px] bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-500/30">
                        <div>
                            <p className="text-[10px] font-bold text-white/80 uppercase tracking-widest mb-1">En Kârlı Ürün</p>
                            <h5 className="text-base font-black truncate max-w-[150px] lg:max-w-[200px]">
                                {analyticsData.topProfit[0]?.name || 'Veri Yok'}
                            </h5>
                            <span className="text-sm font-bold">%{analyticsData.topProfit[0]?.profitMargin.toFixed(1) || 0} Kar Marjı</span>
                        </div>
                        <div className="h-12 w-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
                            <MdTrendingUp className="text-2xl" />
                        </div>
                    </div>
                </div>
            )}

            {/* 3. GRAFİK BÖLÜMÜ (APEXCHARTS) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="flex flex-col bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-2">
                        <span className="text-brand-500"><MdBarChart /></span> Satış Adetleri (Top 5)
                    </h5>
                    {loading ? <ChartSkeleton /> : (
                        <div className="h-80 w-full mt-4">
                            <Chart options={chartData.selling.options} series={chartData.selling.series} type="bar" height="100%" />
                        </div>
                    )}
                </div>

                <div className="flex flex-col bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10">
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-2">
                        <span className="text-green-500"><MdShowChart /></span> Ciro Dağılımı (Top 5)
                    </h5>
                    {loading ? <ChartSkeleton /> : (
                        <div className="h-80 w-full mt-4">
                            <Chart options={chartData.revenue.options} series={chartData.revenue.series} type="bar" height="100%" />
                        </div>
                    )}
                </div>
            </div>

            {/* 4. DETAYLI LİSTELER (SAYFALAMALI) */}
            {loading ? <ListSkeleton /> : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {renderAnalyticsList('topSelling', 'Çok Satanlar', <MdArrowForward className="-rotate-45" />, 'total_sold', 'Adet')}
                    {renderAnalyticsList('topRevenue', 'Ciro Şampiyonları', <MdAttachMoney />, 'totalRevenue', 'Ciro', true)}
                    {renderAnalyticsList('topProfit', 'Kâr Liderleri', <MdTrendingUp />, 'totalProfit', 'Net Kâr', true, true)}
                </div>
            )}

        </div>
    );
};

export default AdminStockAnalytics;